import React, { forwardRef } from 'react';
import {
  PALETTE, FONT_SERIF, FONT_SANS, FONT_MONO,
  HaleMark, ToxIntelMark, Backplate, Card, Eyebrow, Headline, Body, Pill, Bullet, Callout, Rule,
} from './primitives';

/* Asset 1 — Hale Marketplace Overview
 *
 * A presentation-ready visual that simulates the live /hale/marketplace
 * surface with five annotated callouts. The mockup mirrors the actual
 * marketplace card grid (5 categories, coral eyebrows, ivory background)
 * so the slide reads as "this is the real product", not a separate
 * marketing illustration. */

const VB_W = 1600;
const VB_H = 900;

const CATEGORIES = [
  { code: '01', label: 'Reviewed™ Pathway',         services: ['Ingredient transparency', 'Formula governance', 'Reviewer escalation'] },
  { code: '02', label: 'Verified™ Pathway',         services: ['Supplier records', 'Packaging stewardship', 'Documentation maturity'] },
  { code: '03', label: 'Scientific Review',         services: ['Toxicologist assessment', 'Memo + verdict letter', 'Audit trail'] },
  { code: '04', label: 'Packaging & RSL Intel',     services: ['Restricted substance scan', 'Packaging overlays', 'Multi-jurisdiction'] },
  { code: '05', label: 'Regulatory Readiness',      services: ['MoCRA · EU 1223', 'Prop 65 · IFRA · PFAS', 'Retailer documentation'] },
];

const MarketplaceOverviewAsset = forwardRef(function MarketplaceOverviewAsset(_props, ref) {
  return (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width="100%"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Hale Marketplace overview asset"
    >
      <Backplate w={VB_W} h={VB_H} />

      {/* Header strip — mirrors the live app header */}
      <Rule x1={0} x2={VB_W} y={88} />
      <ToxIntelMark x={64} y={56} fontSize={26} />
      <g transform="translate(168,38)">
        <rect width={120} height={26} fill={PALETTE.SLATE} rx={2} />
        <text x={60} y={17} textAnchor="middle" fill={PALETTE.IVORY} fontFamily={FONT_MONO} fontSize={10} letterSpacing={2}>
          · HALE MARKETPLACE
        </text>
      </g>
      <text x={VB_W - 64} y={56} textAnchor="end" fill={PALETTE.MUTED} fontFamily={FONT_MONO} fontSize={10} letterSpacing={2}>
        GATED · APPROVED HALE LEAGUE BRANDS
      </text>

      {/* Hero block */}
      <Eyebrow x={64} y={148} text="Hale Marketplace · curated services catalog" />
      <Headline x={64} y={210} text="A curated services marketplace" size={56} />
      <Headline x={64} y={266} text="for approved Hale League Brands." size={56} italic color={PALETTE.CORAL} />
      <Body
        x={64}
        y={310}
        text={[
          'Five governance categories. Independent scientific review. Documentation portable',
          'across Sephora, Credo, Whole Foods, Target, and Ulta. Built on ToxIntel infrastructure.',
        ]}
        size={17}
        color={PALETTE.MUTED}
        lineHeight={1.55}
      />

      {/* Marketplace card grid — five categories */}
      {CATEGORIES.map((c, i) => {
        const x = 64 + i * 300;
        const y = 420;
        return (
          <g key={c.code} transform={`translate(${x},${y})`}>
            <Card x={0} y={0} w={272} h={360}>
              <g transform="translate(24,28)">
                <Eyebrow x={0} y={0} text={`${c.code} · ${String(c.services.length).padStart(2,'0')} services`} />
                <text x={0} y={64} fill={PALETTE.SLATE} fontFamily={FONT_SERIF} fontWeight={700} fontSize={22}>
                  {c.label}
                </text>
                {c.services.map((s, j) => (
                  <Bullet key={s} x={0} y={110 + j * 36} text={s} size={14} />
                ))}
                <g transform="translate(0,260)">
                  <rect width={224} height={1} fill={PALETTE.RULE} />
                  <text x={0} y={28} fill={PALETTE.CORAL} fontFamily={FONT_MONO} fontSize={10} letterSpacing={2}>
                    OPEN CATEGORY  →
                  </text>
                </g>
              </g>
            </Card>
          </g>
        );
      })}

      {/* Callouts overlaying the cards */}
      <Callout x={180}  y={400} label="Reviewed™ pathway"      sub="Scientific governance activation" anchor="start" />
      <Callout x={500}  y={400} label="Verified™ pathway"      sub="Documentation maturity"            anchor="start" />
      <Callout x={820}  y={400} label="Independent review"     sub="ToxIntel toxicologists"            anchor="start" />
      <Callout x={1140} y={400} label="Packaging + RSL"        sub="Multi-jurisdiction"                anchor="start" />
      <Callout x={1430} y={400} label="Regulatory readiness"   sub="Retailer-ready memos"              anchor="end" />

      {/* Footer */}
      <Rule x1={64} x2={VB_W - 64} y={830} />
      <text x={64} y={860} fill={PALETTE.MUTED} fontFamily={FONT_MONO} fontSize={11} letterSpacing={2}>
        HALE × TOXINTEL · GOVERNANCE INFRASTRUCTURE FOR MODERN BRANDS
      </text>
      <text x={VB_W - 64} y={860} textAnchor="end" fill={PALETTE.MUTED} fontFamily={FONT_MONO} fontSize={11} letterSpacing={2}>
        PILOT LAUNCH · 2026
      </text>
    </svg>
  );
});

export default MarketplaceOverviewAsset;
